import { useFormContext } from "react-hook-form";

import type { InputT } from "@patient-watch/types";
import { ChoiceCard, ChoiceGroup, type AddOwnOptionProps, useOwnOptionRows } from "@patient-watch/forms";
import { FormField, FormItem, FormMessage } from "@patient-watch/ui/form";
import { useId } from "react";

interface Props {
    input: InputT;
    /** "Add your own option", when the question allows it. Saving is the caller's. */
    addOwnOption?: Omit<AddOwnOptionProps, "inputId" | "onAdded" | "multiple">;
}

function normalizeOptions(input: InputT): {
    value: string;
    label: string;
    description?: string | null;
    segments?: { label: string; description?: string | null }[];
    disabled?: boolean;
}[] {
    if (input.scale_options?.length) {
        return input.scale_options.map((o) => ({
            value: String(o.value),
            label: o.label,
            description: o.description,
            segments: o.segments ?? undefined,
            // A dictionary option marked is_disabled is shown but not selectable.
            disabled: (o as { disabled?: boolean }).disabled === true,
        }));
    }
    return (input.options ?? []).map((o) => ({ value: o, label: o }));
}

const MultipleChoice = ({ input, addOwnOption }: Props) => {
    const { control, getValues, setValue } = useFormContext();
    const options = normalizeOptions(input);
    const labelId = `${useId()}-label`;
    const questionLabel = input.input_label?.trim() ?? "";
    const hasQuestionLabel = questionLabel.length > 0;
    const chosen = (): string[] => {
        const value = getValues(input.input_id);
        return Array.isArray(value) ? value : [];
    };
    const answer = { shouldDirty: true, shouldValidate: true };
    const ownRows = useOwnOptionRows({
        addOwnOption,
        inputId: input.input_id,
        multiple: true,
        onAdded: (value) => setValue(input.input_id, [...chosen(), value], answer),
        onDeleted: (value) =>
            setValue(
                input.input_id,
                chosen().filter((v) => v !== value),
                answer
            ),
    });

    return (
        <FormField
            control={control}
            name={input.input_id}
            render={({ field, fieldState }) => (
                <FormItem>
                    <ChoiceCard
                        labelId={labelId}
                        caption={input.input_caption}
                        label={input.input_label}
                        required={!input.is_optional}
                        description={input.input_description}
                        invalid={fieldState.invalid}
                    >
                        <ChoiceGroup
                            name={input.input_id}
                            multiple
                            options={ownRows.decorate(
                                options.map((o) => ({
                                    value: o.value,
                                    label: o.label,
                                    description: o.description,
                                    segments: o.segments,
                                    disabled: o.disabled,
                                }))
                            )}
                            value={Array.isArray(field.value) ? field.value : []}
                            onChange={(next) => field.onChange(next)}
                            invalid={fieldState.invalid}
                            labelledBy={hasQuestionLabel ? labelId : undefined}
                            data-testid={`form-options-${input.input_id}`}
                            footer={ownRows.footer}
                        />
                        <FormMessage />
                    </ChoiceCard>
                </FormItem>
            )}
        />
    );
};

export default MultipleChoice;
